import { useQuery, useReactiveVar } from '@apollo/client';
import { GET_CUSTOMER } from '@queries/getCustomer';
import { emptyCustomer } from 'components/auth/authConstants';
import { showToast } from 'components/common/CustomToast';
import { useAimoPlusEligibility } from 'hooks/useAimoPlusEligibility';
import useCache from 'hooks/useCache';
import { useCustomerSubscriptionData } from 'hooks/useCustomerSubscriptionData';
import { usePermitsAndBenefits } from 'hooks/usePermitsAndBenefits';
import { getLanguage, setLanguage } from 'i18n/init';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  country,
  featureCountryCode,
  showLandingScreen,
} from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useUiStore } from 'stores/uiStore';
import {
  CountryCode,
  Customer,
  Maybe,
  Query,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import {
  DecodedAccessTokenType,
  getDecodedToken,
  isNewAppUser,
} from 'utils/accessTokenUtils';
import { handleCustomerCardIndicator } from 'utils/indicatorUtils';
import Logger from 'utils/Logger';
import { updateStateAndStorage } from 'utils/MMKVStorageUtils';
import AccountContext from './AccountContext';

const handleCustomer = async ({
  customer,
  appCountry,
}: {
  customer: Maybe<Customer> | undefined;
  appCountry: CountryCode | null;
}) => {
  if (customer) {
    updateStateAndStorage(country, 'country', customer.countryCode);
    if (!appCountry) {
      updateStateAndStorage(
        featureCountryCode,
        'featureCountryCode',
        customer.countryCode
      );
    }
    // Adding this back, it was removed in CPS-16354 and resulted in an issue
    // that we only got UID on the RUM sessions, if user continued to use the app
    // If the user killed the app -> opened again, there was no customer info in the rum sessions
    if (customer.uid) {
      Logger.setDatadogUser(customer.uid);
    }

    // Set customer preferred language
    if (
      customer.preferredLanguage &&
      customer.preferredLanguage !== getLanguage()
    ) {
      setLanguage(customer.preferredLanguage);
    }

    // Get indicator related to cards
    handleCustomerCardIndicator(customer.paymentCards);
  }
};

const AccountDetailsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [decodedToken, setDecodedToken] = useState<DecodedAccessTokenType>();
  const token = useAuthStore((state) => state.accessToken);
  const isSignUpFlowOngoing = useUiStore((state) => state.isSignUpFlow);
  const setIsSignUpFlow = useUiStore((state) => state.setIsSignUpFlow);
  const appCountry = useReactiveVar(featureCountryCode);
  const { apolloCache } = useCache();
  const {
    data: { readAppCustomer: customer } = {},
    loading,
    error,
    refetch: refetchCustomer,
  } = useQuery<Query>(GET_CUSTOMER, {
    fetchPolicy: 'cache-and-network',
    skip: !token,
    errorPolicy: 'all',
    onCompleted: () => {
      apolloCache?.gc();
      updateStateAndStorage(showLandingScreen, 'showLandingScreen', false);
    },
  });
  const {
    permits,
    benefits,
    loading: permitsAndBenefitsLoading,
    refetchPermits,
    refetchBenefits,
  } = usePermitsAndBenefits({
    customerUid: customer?.uid || undefined,
  });

  // Fetch customer subscription data and payment status (also handles subscription tab bar indicator)
  const {
    customerSubscription,
    loading: customerSubscriptionLoading,
    refetchCustomerSubscription,
    isSubscriptionPaymentFailed,
  } = useCustomerSubscriptionData({
    aimoPlusSubscriber: !!customer?.aimoPlusSubscriber,
  });

  useAimoPlusEligibility({
    isLoggedIn: !!token,
    aimoPlusSubscriber: !!customer?.aimoPlusSubscriber,
  });

  const isSignUp = isNewAppUser(
    useAuthStore().accessToken ?? '',
    !!customer?.acceptances?.length,
    customer?.accountType ?? 'NEW'
  );
  // Guarded because zustand's `set` notifies subscribers even when the value is
  // unchanged; the reactive var this replaced did not.
  if (isSignUpFlowOngoing !== isSignUp) {
    setIsSignUpFlow(isSignUp);
  }
  useEffect(() => {
    if (!loading) {
      handleCustomer({
        customer,
        appCountry: appCountry,
      });
    }
  }, [appCountry, customer, loading]);

  useEffect(() => {
    if (token) {
      setDecodedToken(getDecodedToken(token) as DecodedAccessTokenType);
    } else {
      setDecodedToken(undefined);
    }
  }, [token]);

  useEffect(() => {
    // Suppress warning error message if no PMC was found.
    if (
      (error as any)?.graphQLErrors[0]?.extensions?.level === 'warn' &&
      error?.message.includes('PMC not found')
    ) {
      return;
    }

    if (token && error?.message) {
      const hasAcceptTheTerms = !!customer?.acceptances?.length;
      if (hasAcceptTheTerms) {
        if (customer) {
          showToast('error.readCustomerInfo', 'info');
        } else {
          showToast('error.readCustomerError', 'error');
        }
      }
    }
  }, [error, error?.message, token, customer]);

  const accountContext = useMemo(() => {
    const memberGroupUids = Array.from(
      new Set(
        (benefits ?? [])
          .map((benefit) => benefit?.memberGroupUid)
          .filter((uid): uid is string => !!uid)
      )
    );

    return {
      customer: {
        ...(customer || emptyCustomer),
        companyBenefits: benefits?.filter(
          (benefit) => benefit?.benefitTargetType === 'USE_CORPORATE_ACCOUNT'
        ),
        householdBenefits: benefits?.filter(
          (benefit) => benefit?.benefitTargetType === 'USE_PMC'
        ),
        poolingBenefits: benefits?.filter(
          (benefit) => benefit?.benefitTargetType === 'USE_POOL'
        ),
        permits: permits?.filter(Boolean) as UnifiedPermit[],
      },
      decodedToken,
      error,
      loading,
      benefitsLoading: loading || permitsAndBenefitsLoading,
      memberGroupUids,
      refetchCustomer,
      refetchPermits,
      refetchBenefits,
      customerSubscription,
      customerSubscriptionLoading,
      refetchCustomerSubscription,
      isSubscriptionPaymentFailed,
    };
  }, [
    customer,
    error,
    permits,
    benefits,
    loading,
    permitsAndBenefitsLoading,
    refetchCustomer,
    refetchPermits,
    refetchBenefits,
    decodedToken,
    customerSubscription,
    customerSubscriptionLoading,
    refetchCustomerSubscription,
    isSubscriptionPaymentFailed,
  ]);

  return (
    <AccountContext.Provider value={accountContext}>
      <>{children}</>
    </AccountContext.Provider>
  );
};
export default AccountDetailsProvider;
